import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { Add01Icon, Cancel01Icon, Notification01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { Permissions } from 'constants/permissions';
import { getRecentTagIds, saveRecentTagId } from 'helpers/recentTags';
import patternValidators, { emailFieldError } from 'helpers/validators/pattern-validators';
import { useAccessControl } from 'hooks/access-control';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { CustomFields } from 'views/Company/tabs/CustomFields/components/CustomFields/CustomFields';

import {
  Input,
  InputGroup,
  PhoneNumberPicker,
  Select,
  ToggleSwitchInput,
} from 'components/FormComponents';
import { TagSearchDropdown } from 'components/TagSearchDropdown/TagSearchDropdown';
import { CustomToast } from 'components/Toast/CustomToast';

import { apiErrorMessage, createTag } from 'api/tags-slice';
import { selectPermissions } from 'api/user-permissions-slice';

import styles from './CandidateDetailsForm.module.scss';

export const CandidateDetailsForm = forwardRef(
  (
    {
      company = {},
      tags,
      defaultValues = {},
      teams = [],
      customFields = [],
      className,
      users = [],
      onSubmit = () => {},
      setInvalid = () => {},
    },
    ref,
  ) => {
    const {
      register,
      watch,
      reset,
      control,
      setValue,
      getValues,
      handleSubmit,
      formState: { errors },
    } = useForm({ defaultValues, shouldUnregister: true });

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const permissions = useSelector(selectPermissions);
    const hasAccess = useAccessControl(permissions);
    const [ccNotificationEmailsEnabled, setCcNotificationEmailsEnabled] = useState(false);
    const [tagDropdownVisible, setTagDropdownVisible] = useState(false);
    const tagTriggerRef = useRef(null);

    const team_id = useWatch({ control, name: 'team_id' });

    // Nullify cc emails if toggled off
    useEffect(() => {
      if (!ccNotificationEmailsEnabled) setValue('cc_notification_emails', '');
    }, [ccNotificationEmailsEnabled]);

    // Reset if new default values
    useEffect(() => {
      reset(defaultValues);
    }, [JSON.stringify(defaultValues)]);

    // Set validation
    useEffect(() => {
      setInvalid(Object.keys(errors).length > 0);
    }, [errors]);

    // Expose functions
    useImperativeHandle(ref, () => ({
      submit: () => {
        handleSubmit(onSubmit)();
      },
    }));

    return (
      <form className={cn({ [className]: className })}>
        <div className={cn('card', 'card-with-border', 'u-padding', 'u-margin-bottom')}>
          <div className="inline u-margin-bottom">
            <InputGroup title="First Name *">
              <Input
                name="first_name"
                placeholder="e.g. Brad"
                register={register}
                validators={{
                  required: true,
                  ...patternValidators.name,
                }}
                error={errors?.first_name?.type}
                inputProps={{ tabIndex: 1 }}
                dataTestId="create-check-candidate-first-name"
              />
            </InputGroup>
            <InputGroup title="Last Name *">
              <Input
                name="last_name"
                placeholder="e.g. Pitt"
                register={register}
                validators={{
                  required: true,
                  ...patternValidators.name,
                }}
                error={errors?.last_name?.type}
                inputProps={{ tabIndex: 2 }}
                dataTestId="create-check-candidate-last-name"
              />
            </InputGroup>
          </div>
          <InputGroup title="Email Address *" className="u-margin-bottom--large">
            <Input
              name="email"
              placeholder="e.g. brad@checkmate.tech"
              register={register}
              validators={{
                required: true,
                ...patternValidators.email,
              }}
              error={emailFieldError(errors?.email)}
              inputProps={{ tabIndex: 3 }}
              dataTestId="create-check-candidate-email"
            />
          </InputGroup>
          <InputGroup title="Contact Number" className="u-margin-bottom--large">
            <Controller
              control={control}
              name="contact_number"
              render={({ field: { onChange, value } }) => (
                <PhoneNumberPicker
                  value={value}
                  onChange={onChange}
                  tabIndex={4}
                  defaultCountry={company?.country}
                  dataTestId="create-check-candidate-contact-number"
                  countrySelectTestId="phone-number-country-select"
                  numberInputTestId="phone-number-input"
                />
              )}
            />
          </InputGroup>
          <InputGroup title="Job Title" className="u-margin-bottom--large">
            <Input
              name="job_role"
              placeholder="Mastermind"
              register={register}
              inputProps={{ tabIndex: 5 }}
              dataTestId="create-check-candidate-job-role"
            />
          </InputGroup>
          <InputGroup title="Department" className="u-margin-bottom--large">
            <Input
              name="department"
              placeholder="Oceans 11"
              register={register}
              inputProps={{ tabIndex: 6 }}
              dataTestId="create-check-candidate-department"
            />
          </InputGroup>
          <InputGroup title="Reference Code" className="u-margin-bottom--large">
            <Input
              name="reference_code"
              placeholder="REFCODE12"
              register={register}
              inputProps={{ tabIndex: 7 }}
              dataTestId="create-check-candidate-reference-code"
            />
          </InputGroup>

          {teams.length > 1 && (
            <InputGroup title="Team" className="u-margin-bottom--large">
              <Select
                name="team_id"
                placeholder="Team"
                register={register}
                value={team_id}
                useDefault>
                {teams.map(team => (
                  <Select.Item key={team.id} value={team.id}>
                    {team.name}
                  </Select.Item>
                ))}
              </Select>
            </InputGroup>
          )}
        </div>
        {tags !== undefined && (
          <div className={cn('card', 'card-with-border', 'u-padding', 'u-margin-bottom')}>
            <div className="u-width-100">
              <div className={cn('u-bold')}>Tags</div>
              <InputGroup title="Tags to be added to the candidate. This can be updated at any time.">
                <Controller
                  control={control}
                  name="tag_ids"
                  render={({ field: { onChange, value } }) => {
                    const selectedIds = (value || []).map(Number);
                    const selectedTags = selectedIds
                      .map(id => (tags || []).find(t => Number(t.id) === id))
                      .filter(Boolean);

                    const recentTags = getRecentTagIds()
                      .map(rid => (tags || []).find(t => Number(t.id) === rid))
                      .filter(Boolean);

                    const handleSelect = tagId => {
                      const numId = Number(tagId);
                      // Read the latest value (not the render-closure selectedIds) so rapid
                      // picks made before React commits the re-render don't overwrite each other.
                      const latestIds = (getValues('tag_ids') || []).map(Number);
                      if (latestIds.includes(numId)) {
                        onChange(latestIds.filter(id => id !== numId));
                      } else {
                        onChange([...latestIds, numId]);
                        saveRecentTagId(numId);
                      }
                      // keep dropdown open so user can pick multiple
                    };

                    const handleCreate = async ({ name, color }) => {
                      try {
                        const newTag = await dispatch(createTag({ name, color })).unwrap();
                        if (!newTag?.id) {
                          throw new Error('Tag was created but no id was returned.');
                        }
                        // Read the latest value at resolve time (not the render-closure
                        // selectedIds) so picks made while the create was in flight aren't lost.
                        const latestIds = (getValues('tag_ids') || []).map(Number);
                        const newId = Number(newTag.id);
                        onChange(latestIds.includes(newId) ? latestIds : [...latestIds, newId]);
                        saveRecentTagId(newTag.id);
                      } catch (e) {
                        const errMsg = apiErrorMessage(e, 'Please try again.');
                        toast.custom(toastInstance => (
                          <CustomToast
                            t={toastInstance}
                            title="Could not create tag"
                            message={errMsg}
                            type="error"
                          />
                        ));
                        // Re-throw so the dropdown keeps the typed name for retry.
                        throw e;
                      }
                      // keep dropdown open so user can continue picking
                    };

                    const handleRemove = tagId => {
                      // Read the latest value (not the render-closure selectedIds) so rapid
                      // chip removals before React commits the re-render don't restore each other.
                      const latestIds = (getValues('tag_ids') || []).map(Number);
                      onChange(latestIds.filter(id => id !== Number(tagId)));
                    };

                    return (
                      <div className={styles.tagBox}>
                        {selectedTags.map(tag => (
                          <span key={tag.id} className={styles.tagChip}>
                            <span className={styles.tagDot} style={{ '--tag-color': tag.color }} />
                            {tag.name}
                            <span
                              className={styles.tagRemove}
                              onClick={e => {
                                e.stopPropagation();
                                handleRemove(tag.id);
                              }}>
                              <HugeiconsIcon icon={Cancel01Icon} size="12" strokeWidth="2.5" />
                            </span>
                          </span>
                        ))}
                        <div
                          ref={tagTriggerRef}
                          onClick={() => setTagDropdownVisible(v => !v)}
                          className={styles.addTagBtn}>
                          <HugeiconsIcon icon={Add01Icon} size={12} strokeWidth="2.5" />
                          {t('tags.addTag')}
                          <TagSearchDropdown
                            visible={tagDropdownVisible}
                            usePortal
                            tags={tags || []}
                            allTagsForNameCheck={tags || []}
                            recentTags={recentTags}
                            selectedIds={selectedIds}
                            onSelect={handleSelect}
                            onCreate={hasAccess(Permissions.TagsWrite) ? handleCreate : undefined}
                            onClose={() => setTagDropdownVisible(false)}
                            triggerRef={tagTriggerRef}
                          />
                        </div>
                      </div>
                    );
                  }}
                />
              </InputGroup>
            </div>
          </div>
        )}
        <div className={cn('card', 'card-with-border', 'u-padding', 'u-margin-bottom')}>
          <div className="u-width-100">
            <div className={cn('u-bold')}>User Assignment</div>
            <InputGroup title="The user who is assigned to manage the candidate and receive notifications. This can be updated at any time.">
              <Select
                name="user_id"
                placeholder="User"
                register={register}
                value={watch('user_id')}
                useDefault
                useSearch>
                {(users || []).map(user => (
                  <Select.Item key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} • {user.email}
                  </Select.Item>
                ))}
              </Select>
            </InputGroup>
          </div>
        </div>
        {customFields.length > 0 && (
          <div className={cn('card', 'card-with-border', 'u-padding', 'u-margin-bottom')}>
            <div className="u-width-100">
              <CustomFields
                fields={customFields}
                register={register}
                control={control}
                watch={watch}
                errors={errors}
              />
            </div>
          </div>
        )}
        <ToggleSwitchInput
          text="Notification List"
          subtext="Add external emails to get notified about status updates"
          onChange={e => setCcNotificationEmailsEnabled(e.target.checked)}
          icon={Notification01Icon}
          value={JSON.parse(ccNotificationEmailsEnabled)}>
          <InputGroup title="Emails">
            <Input
              name="cc_notification_emails"
              placeholder="email_1@example.com,email_2@example.com"
              register={register}
              validators={patternValidators.emailList}
              error={errors?.cc_notification_emails?.message}
            />
          </InputGroup>
        </ToggleSwitchInput>
      </form>
    );
  },
);
